import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Button from '../components/Button';
import Input from '../components/Input';
import Pill from '../components/Pill';
import { EventSales, organizerSalesService, StageSales } from '../services/organizerSalesService';
import { DATE_TIME_SHORT, formatEventDate, formatInstant } from '../utils/dates';

// Near real time: sales are confirmed seconds after each payment
const REFRESH_MS = 30_000;

type StatusFilter = 'ACTIVE' | 'FINISHED' | 'all';

interface Totals {
    capacity: number;
    courtesy: number;
    // Capacity minus courtesy tickets: what the sold share is measured against
    onSale: number;
    sold: number;
    reserved: number;
    available: number;
}

const courtesyOf = (stage: StageSales) => stage.courtesyQuantity ?? 0;

const onSaleOf = (stage: StageSales) => Math.max(stage.capacity - courtesyOf(stage), 0);

const sumStages = (stages: StageSales[]): Totals => stages.reduce(
    (acc, stage) => ({
        capacity: acc.capacity + stage.capacity,
        courtesy: acc.courtesy + courtesyOf(stage),
        onSale: acc.onSale + onSaleOf(stage),
        sold: acc.sold + stage.sold,
        reserved: acc.reserved + stage.reserved,
        available: acc.available + stage.available
    }),
    { capacity: 0, courtesy: 0, onSale: 0, sold: 0, reserved: 0, available: 0 });

const percent = (part: number, total: number) => total > 0 ? Math.min(100, Math.round((part / total) * 100)) : 0;

const formatNumber = (value: number) => new Intl.NumberFormat('es-CO').format(value);

const formatCurrency = (amount: number) =>
    new Intl.NumberFormat('es-CO', { style: 'currency', currency: 'COP', maximumFractionDigits: 0 }).format(amount);

// The event's wall time, shown as the organizer typed it
const formatDate = (dateString?: string) => formatEventDate(dateString, DATE_TIME_SHORT, 'Fecha por definir');

const statusBadge = (status: EventSales['status']) => {
    switch (status) {
        case 'ACTIVE': return <span className="st-badge st-badge-success st-badge-dot">Activo</span>;
        case 'FINISHED': return <span className="st-badge st-badge-neutral">Finalizado</span>;
        case 'CANCELLED': return <span className="st-badge st-badge-error">Cancelado</span>;
        default: return <span className="st-badge st-badge-neutral">{status}</span>;
    }
};

const ProgressBar = ({ value }: { value: number }) => (
    <div className="w-full h-2 bg-[var(--st-bg-tertiary)] rounded-full overflow-hidden border border-[var(--st-border)]">
        <div className="h-full bg-[var(--st-accent-primary)] transition-all" style={{ width: `${value}%` }}></div>
    </div>
);

const SummaryCard = ({ label, value, hint }: { label: string; value: string; hint?: string }) => (
    <div className="st-card-static p-5">
        <p className="text-xs text-[var(--st-text-muted)] uppercase font-bold tracking-wider mb-1">{label}</p>
        <p className="font-display text-2xl font-bold text-[var(--st-text-primary)]">{value}</p>
        {hint && <p className="text-xs text-[var(--st-text-muted)] mt-1">{hint}</p>}
    </div>
);

const EventSalesCard = ({ event }: { event: EventSales }) => {
    const totals = sumStages(event.stages);
    const soldPercent = percent(totals.sold, totals.onSale);

    return (
        <div className="st-card-static overflow-hidden">
            <div className="p-5 border-b border-[var(--st-border)] flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div>
                    <div className="flex items-center gap-3 flex-wrap mb-1">
                        <h2 className="font-display text-lg font-bold text-[var(--st-text-primary)]">{event.name}</h2>
                        {statusBadge(event.status)}
                    </div>
                    <p className="text-sm text-[var(--st-text-secondary)]">
                        {formatDate(event.startDate)}{event.location ? ` · ${event.location}` : ''}
                    </p>
                </div>
                <div className="w-full md:w-64">
                    <div className="flex justify-between text-sm mb-1">
                        <span className="text-[var(--st-text-secondary)]">
                            <span className="font-bold text-[var(--st-text-primary)]">{formatNumber(totals.sold)}</span> de {formatNumber(totals.onSale)} vendidas
                        </span>
                        <span className="font-bold text-[var(--st-accent-primary)]">{soldPercent}%</span>
                    </div>
                    <ProgressBar value={soldPercent} />
                </div>
            </div>

            {event.stages.length === 0 ? (
                <p className="p-5 text-sm text-[var(--st-text-muted)]">Este evento no tiene etapas.</p>
            ) : (
                <div className="overflow-x-auto">
                    <table className="w-full text-left border-collapse text-sm">
                        <thead>
                            <tr className="bg-[var(--st-bg-tertiary)] text-xs uppercase text-[var(--st-text-muted)] tracking-wider">
                                <th className="p-4 font-bold">Etapa</th>
                                <th className="p-4 font-bold text-right">Precio</th>
                                <th className="p-4 font-bold text-right">Aforo</th>
                                <th className="p-4 font-bold text-right" title="Salen del aforo: no se venden">Cortesías</th>
                                <th className="p-4 font-bold text-right">Vendidas</th>
                                <th className="p-4 font-bold text-right" title="Compradores pagando en este momento: se liberan si no pagan">En pago</th>
                                <th className="p-4 font-bold text-right">Disponibles</th>
                                <th className="p-4 font-bold w-40" title="Sobre las boletas a la venta: aforo menos cortesías">% vendido</th>
                            </tr>
                        </thead>
                        <tbody>
                            {event.stages.map(stage => {
                                const stagePercent = percent(stage.sold, onSaleOf(stage));
                                return (
                                    <tr key={stage.stageId} className="border-t border-[var(--st-border)]">
                                        <td className="p-4">
                                            <span className="font-medium text-[var(--st-text-primary)]">{stage.name}</span>
                                            {!stage.active && <span className="st-badge st-badge-neutral text-xs ml-2">Inactiva</span>}
                                        </td>
                                        <td className="p-4 text-right text-[var(--st-text-secondary)]">{formatCurrency(stage.price)}</td>
                                        <td className="p-4 text-right text-[var(--st-text-secondary)]">{formatNumber(stage.capacity)}</td>
                                        <td className="p-4 text-right text-[var(--st-text-secondary)]">{formatNumber(courtesyOf(stage))}</td>
                                        <td className="p-4 text-right font-bold text-[var(--st-text-primary)]">{formatNumber(stage.sold)}</td>
                                        <td className="p-4 text-right text-[var(--st-text-secondary)]">{formatNumber(stage.reserved)}</td>
                                        <td className="p-4 text-right text-[var(--st-text-secondary)]">
                                            {stage.available === 0
                                                ? <span className="st-badge st-badge-error text-xs">Agotada</span>
                                                : formatNumber(stage.available)}
                                        </td>
                                        <td className="p-4">
                                            <div className="flex items-center gap-2">
                                                <ProgressBar value={stagePercent} />
                                                <span className="text-xs font-bold text-[var(--st-text-secondary)] w-9 text-right">{stagePercent}%</span>
                                            </div>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
};

export default function OrganizerSales() {
    const navigate = useNavigate();

    const [events, setEvents] = useState<EventSales[] | null>(null);
    const [error, setError] = useState(false);
    const [isRefreshing, setIsRefreshing] = useState(false);
    const [updatedAt, setUpdatedAt] = useState<Date | null>(null);
    const [filter, setFilter] = useState<StatusFilter>('ACTIVE');
    const [searchTerm, setSearchTerm] = useState('');
    const refreshTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

    const load = useCallback(async () => {
        if (refreshTimer.current) clearTimeout(refreshTimer.current);
        setIsRefreshing(true);
        try {
            setEvents(await organizerSalesService.getMySales());
            setUpdatedAt(new Date());
            setError(false);
        } catch {
            // Keeps the last data on screen: the next refresh may work
            setError(true);
        } finally {
            setIsRefreshing(false);
            // Only while the tab is visible; it refreshes as soon as it becomes visible again
            if (document.visibilityState === 'visible') {
                refreshTimer.current = setTimeout(load, REFRESH_MS);
            }
        }
    }, []);

    useEffect(() => {
        load();
        const onVisibilityChange = () => {
            if (document.visibilityState === 'visible') load();
        };
        document.addEventListener('visibilitychange', onVisibilityChange);
        return () => {
            document.removeEventListener('visibilitychange', onVisibilityChange);
            if (refreshTimer.current) clearTimeout(refreshTimer.current);
        };
    }, [load]);

    const filteredEvents = (events ?? []).filter(event =>
        (filter === 'all' || event.status === filter)
        && event.name.toLowerCase().includes(searchTerm.toLowerCase()));
    const totals = sumStages(filteredEvents.flatMap(event => event.stages));

    if (events === null) {
        return (
            <div className="st-page py-12">
                <div className="st-container">
                    {error ? (
                        <div className="st-card-static p-8 text-center">
                            <h1 className="font-display text-2xl font-bold text-[var(--st-text-primary)] mb-2">No pudimos cargar tus ventas</h1>
                            <p className="text-[var(--st-text-secondary)] mb-6">Intenta de nuevo en unos segundos.</p>
                            <Button className="st-btn st-btn-primary" onClick={load} disabled={isRefreshing}>
                                {isRefreshing ? 'Consultando...' : 'Intentar de Nuevo'}
                            </Button>
                        </div>
                    ) : (
                        <div className="flex items-center justify-center min-h-[40vh]"><div className="st-spinner st-spinner-xl"></div></div>
                    )}
                </div>
            </div>
        );
    }

    return (
        <div className="st-page py-12">
            <div className="st-container">

                {/* HEADER */}
                <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-8">
                    <div>
                        <h1 className="font-display text-3xl font-bold text-[var(--st-text-primary)]">Ventas</h1>
                        <p className="text-[var(--st-text-secondary)]">
                            Boletas vendidas por etapa de cada evento. Se actualiza cada 30 segundos.
                        </p>
                    </div>
                    <div className="flex items-center gap-3">
                        {updatedAt && (
                            <span className="text-xs text-[var(--st-text-muted)]">
                                Actualizado a las {formatInstant(updatedAt, { hour: 'numeric', minute: '2-digit', second: '2-digit' })}
                            </span>
                        )}
                        <Button variant="secondary" className="st-btn st-btn-md" onClick={load} disabled={isRefreshing}>
                            {isRefreshing ? 'Actualizando...' : 'Actualizar'}
                        </Button>
                    </div>
                </div>

                {error && (
                    <div className="st-alert st-alert-warning mb-6">
                        <div className="st-alert-content">
                            <p className="st-alert-message">
                                No pudimos actualizar las ventas. Mostramos los últimos datos; volveremos a intentarlo en unos segundos.
                            </p>
                        </div>
                    </div>
                )}

                {events.length === 0 ? (
                    <div className="st-card-static p-10 text-center">
                        <h2 className="font-display text-xl font-bold text-[var(--st-text-primary)] mb-2">Aún no tienes eventos publicados</h2>
                        <p className="text-[var(--st-text-secondary)] mb-6">Las ventas aparecen aquí cuando publicas un evento.</p>
                        <Button className="st-btn st-btn-primary" onClick={() => navigate('/organizer/events')}>Ir a Mis Eventos</Button>
                    </div>
                ) : (
                    <>
                        {/* RESUMEN */}
                        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                            <SummaryCard label="Vendidas" value={formatNumber(totals.sold)}
                                         hint={`${percent(totals.sold, totals.onSale)}% de las boletas a la venta`} />
                            <SummaryCard label="En pago" value={formatNumber(totals.reserved)}
                                         hint="Compradores pagando ahora" />
                            <SummaryCard label="Disponibles" value={formatNumber(totals.available)} />
                            <SummaryCard label="Aforo" value={formatNumber(totals.capacity)}
                                         hint={totals.courtesy > 0 ? `${formatNumber(totals.courtesy)} de cortesía` : undefined} />
                        </div>

                        {/* FILTROS */}
                        <div className="st-card-static p-4 mb-6 flex flex-col md:flex-row items-center gap-4">
                            <div className="w-full md:w-80">
                                <Input
                                    placeholder="Buscar evento por nombre..."
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    icon={<svg fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" /></svg>}
                                />
                            </div>
                            <div className="flex gap-2 overflow-x-auto w-full md:w-auto pb-2 md:pb-0">
                                <Pill active={filter === 'ACTIVE'} onClick={() => setFilter('ACTIVE')}>Activos</Pill>
                                <Pill active={filter === 'FINISHED'} onClick={() => setFilter('FINISHED')}>Finalizados</Pill>
                                <Pill active={filter === 'all'} onClick={() => setFilter('all')}>Todos</Pill>
                            </div>
                        </div>

                        {filteredEvents.length === 0 ? (
                            <p className="text-center text-[var(--st-text-muted)] py-10">No hay eventos con este filtro.</p>
                        ) : (
                            <div className="space-y-6">
                                {filteredEvents.map(event => <EventSalesCard key={event.eventId} event={event} />)}
                            </div>
                        )}

                        <p className="text-xs text-[var(--st-text-muted)] mt-6">
                            Una boleta cuenta como vendida cuando su pago es confirmado. Los pagos en revisión (por ejemplo,
                            aprobados después de vencer la reserva) no se cuentan hasta resolverse.
                        </p>
                    </>
                )}
            </div>
        </div>
    );
}
